import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useQueryErrorResetBoundary, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Alert, Box, Button, Group, Stack } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { CustomerSystemList } from '@/features/customers/components/CustomerSystemList';
import { CustomerHeader } from '@/features/customers/components/CustomerHeader';
import { CustomerHardwareList } from '@hardwareUnits/components/CustomerHardwareList';
import { useEffect } from 'react';
import { customerQueryOptions } from "@customers/api/customerQueries.ts";

const ParamsSchema = z.object({
  customerId: z.string(),
});

export const Route = createFileRoute('/_authed/customers/$customerId/')({
  params: {
    parse: ParamsSchema.parse,
    stringify: ({customerId}) => ({customerId}),
  },
  loader: ({context: {queryClient}, params: {customerId}}) =>
    queryClient.ensureQueryData(customerQueryOptions(customerId)),
  errorComponent: CustomerDetailErrorComponent,
  component: CustomerDetailPage,
});

function CustomerDetailPage() {
  const {customerId} = Route.useParams();
  const {data: customer} = useSuspenseQuery(customerQueryOptions(customerId));

  return (
    <Stack gap="lg">
      <CustomerHeader
        customer={customer}
      />

      <Box pl="2.375rem">
        <CustomerSystemList customer={customer}/>
      </Box>

      <Box pl="2.375rem">
        <CustomerHardwareList customer={customer}/>
      </Box>
    </Stack>
  );
}

function CustomerDetailErrorComponent({error}: { error: Error }) {
  const router = useRouter()
  const queryErrorResetBoundary = useQueryErrorResetBoundary()

  useEffect(() => {
    queryErrorResetBoundary.reset()
  }, [queryErrorResetBoundary])

  return (
    <Stack gap="lg">
      <Alert icon={<IconInfoCircle size={16}/>}
             title="Error Loading Customer"
             color="red"
             variant="light">
        {error.message || 'Unable to load customer details. Please try again.'}
        <Group mt="md">
          <Button
            variant="light"
            onClick={() => router.invalidate()}
          >
            Retry
          </Button>
          <Button
            variant="outline"
            onClick={() => router.navigate({to: '/customers'})}
          >
            Return to Customer List
          </Button>
        </Group>
      </Alert>
    </Stack>
  )
}